interface MonthlyActivityChartProps {
  activeDays: number;
  changePercentage: number;
  weeklyData?: number[]; // Array of weekly distance values [W1, W2, W3, W4, W5]
}

/**
 * Generate SVG path from weekly data points
 * Creates a smooth line chart with proper scaling
 */
function generateChartPath(data: number[]): string {
  if (!data || data.length === 0) return "";

  const width = 472; // SVG viewBox width
  const height = 150; // SVG viewBox height
  const padding = 10; // Vertical padding

  // Find max value for scaling (minimum 1 to avoid division by zero)
  const maxValue = Math.max(...data, 1);

  // Calculate x positions for each data point
  const pointCount = data.length;
  const xStep = width / (pointCount - 1 || 1);

  // Scale y values (inverted because SVG y=0 is top)
  const points = data.map((value, index) => {
    const x = index * xStep;
    // Scale to fit within height - padding, inverted
    const y = height - padding - (value / maxValue) * (height - 2 * padding);
    return { x, y };
  });

  // Generate smooth curve using cardinal spline approximation
  if (points.length === 1) {
    return `M${points[0].x} ${points[0].y}`;
  }

  let path = `M${points[0].x} ${points[0].y}`;

  // Use smooth curves between points
  for (let i = 0; i < points.length - 1; i++) {
    const current = points[i];
    const next = points[i + 1];

    // Control point distance (30% of segment width)
    const cpOffset = (next.x - current.x) * 0.3;

    // Create smooth bezier curve
    const cp1x = current.x + cpOffset;
    const cp1y = current.y;
    const cp2x = next.x - cpOffset;
    const cp2y = next.y;

    path += `C${cp1x} ${cp1y} ${cp2x} ${cp2y} ${next.x} ${next.y}`;
  }

  return path;
}

export function MonthlyActivityChart({
  activeDays,
  changePercentage,
  weeklyData = [0, 0, 0, 0, 0],
}: MonthlyActivityChartProps) {
  // Generate dynamic path from weekly data
  const chartPath = generateChartPath(weeklyData);

  // For the fill area, we need to close the path to the bottom
  const maxVal = Math.max(...weeklyData, 1);
  const firstPointY = 150 - 10 - ((weeklyData[0] || 0) / maxVal) * 130;
  const fillPath = chartPath ? `${chartPath}V150H0V${firstPointY}Z` : "";

  const isPositive = changePercentage >= 0;

  // Determine if we have any data
  const hasData = weeklyData.some((v) => v > 0);

  return (
    <div className="flex w-full flex-col gap-2 rounded-xl border border-gray-200 dark:border-white/10 bg-card-light dark:bg-black/20 p-6">
      <p className="text-gray-900 dark:text-white text-base font-medium leading-normal">
        Monthly Activity
      </p>
      <p className="text-gray-900 dark:text-white tracking-light text-[32px] font-bold leading-tight truncate">
        {activeDays} {activeDays === 1 ? "Day" : "Days"}
      </p>
      <div className="flex gap-1 items-center">
        <p className="text-gray-500 dark:text-gray-400 text-base font-normal leading-normal">
          Last 90 Days
        </p>
        <p
          className={`text-base font-medium leading-normal ${
            isPositive ? "text-primary" : "text-red-500"
          }`}
        >
          {isPositive ? "+" : ""}
          {changePercentage}%
        </p>
      </div>
      <div className="flex min-h-[180px] flex-1 flex-col gap-8 py-4">
        {hasData ? (
          <svg
            fill="none"
            height="148"
            preserveAspectRatio="none"
            viewBox="-3 0 478 150"
            width="100%"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient
                gradientUnits="userSpaceOnUse"
                id="paint0_linear_activity"
                x1="236"
                x2="236"
                y1="1"
                y2="149"
              >
                <stop stopColor="#0df20d" stopOpacity="0.3" />
                <stop offset="1" stopColor="#0df20d" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={fillPath} fill="url(#paint0_linear_activity)" />
            <path
              d={chartPath}
              stroke="#0df20d"
              strokeLinecap="round"
              strokeWidth="3"
            />
          </svg>
        ) : (
          <div className="flex items-center justify-center h-[148px] text-gray-400 dark:text-gray-500">
            No activity data yet
          </div>
        )}
        <div className="flex justify-around">
          <p className="text-gray-500 dark:text-gray-400 text-[13px] font-bold leading-normal tracking-[0.015em]">
            W1
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-[13px] font-bold leading-normal tracking-[0.015em]">
            W2
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-[13px] font-bold leading-normal tracking-[0.015em]">
            W3
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-[13px] font-bold leading-normal tracking-[0.015em]">
            W4
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-[13px] font-bold leading-normal tracking-[0.015em]">
            W5
          </p>
        </div>
      </div>
    </div>
  );
}
